import { getPartnerWebsiteContent, getWebsiteContent } from './websiteUtils';
import { normalizePartnerSlug, resolvePartnerConfig, resolvePartnerFromHost } from './partnerUtils';

const isBrowser = () => typeof window !== 'undefined';

const safeJoin = (base, suffix) => {
  const left = String(base || '').replace(/\/+$/, '');
  const right = String(suffix || '').replace(/^\/+/, '');
  if (!left) return `/${right}`;
  if (!right) return left || '/';
  return `${left}/${right}`;
};

const getPartnerKey = (partner, fallbackSlug) => {
  const raw =
    partner?.slug ||
    partner?.partnerSlug ||
    partner?.partner ||
    partner?.publicSlug ||
    partner?.vanitySlug ||
    fallbackSlug ||
    '';
  return normalizePartnerSlug(raw);
};

export const resolveShopContext = (settings, { partnerSlug, locale, pathname } = {}) => {
  const normalizedParam = partnerSlug ? normalizePartnerSlug(partnerSlug) : '';
  let partner = null;
  let source = 'global';

  if (normalizedParam) {
    partner = resolvePartnerConfig(settings, normalizedParam);
    source = 'param';
  } else if (isBrowser()) {
    partner = resolvePartnerFromHost(settings, window.location.hostname);
    if (partner) source = 'host';
  }

  const partnerKey = partner ? getPartnerKey(partner, normalizedParam) : null;
  const currentPath = pathname || (isBrowser() ? window.location.pathname : '');

  // For `/partners/:partnerSlug/...` routes we want the base to be `/partners/<slug>`.
  // For `/:partnerSlug/...` routes we want base `/ <slug>`.
  let prefix = '';
  if (normalizedParam) {
    const partnersPrefix = `/partners/${normalizedParam}`;
    prefix = currentPath.startsWith(partnersPrefix) ? partnersPrefix : `/${normalizedParam}`;
  }

  const hasPartner = Boolean(partner);
  const home = prefix || '/';
  const booking = hasPartner ? (prefix ? safeJoin(prefix, 'book') : '/book') : '/addbookings';
  const shop = prefix ? safeJoin(prefix, 'shop') : '/shop';
  const cart = prefix ? safeJoin(prefix, 'cart') : '/cart';
  const checkout = prefix ? safeJoin(prefix, 'checkout') : '/checkout';
  const subscriptions = prefix ? safeJoin(prefix, 'subscriptions') : '/subscriptions';
  const auctions = prefix ? safeJoin(prefix, 'auctions') : '/auctions';

  const websiteContent = hasPartner
    ? getPartnerWebsiteContent(settings, partner, locale)
    : getWebsiteContent(settings, locale);

  // Used for per-partner cart storage.
  const cartScopeKey = partnerKey ? `partner:${partnerKey}` : 'global';

  const prefixPartnerLink = (href) => {
    if (!prefix) return href;
    if (!href) return href;
    const path = String(href);
    if (!path.startsWith('/')) return path;
    if (path === '/') return prefix;
    const prefixable = ['/shop', '/cart', '/checkout', '/subscriptions', '/auctions', '/book'];
    if (!prefixable.some((needle) => path === needle || path.startsWith(`${needle}/`))) return path;
    if (path.startsWith(prefix)) return path;
    return safeJoin(prefix, path);
  };

  return {
    partner,
    partnerKey,
    source,
    prefix,
    cartScopeKey,
    paths: { home, booking, shop, cart, checkout, subscriptions, auctions },
    websiteContent,
    prefixPartnerLink,
  };
};

